import React, { ChangeEvent, FC } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { PhoneInput } from 'transfix-ui/components/PhoneInput'
import { TextArea } from 'transfix-ui/components/TextArea'
import {
  AddEntityForm,
  AddEntityFormFooter,
} from 'apps/fleet-planner/components/forms/AddEntityForm'
import { IAddNewEntityFormProps } from 'apps/fleet-planner/components/forms/TrailerForm'
import {
  ContactWithNotesSchema,
  ContactWithNotesSchemaType,
} from 'apps/fleet-planner/schema/ContactWithNotesSchema'
import { TextInput } from 'components/atoms'
import { Button } from 'transfix-ui'
import formatPhone, { unformatPhone } from 'utils/formatPhone'

const emptyContactState: ContactWithNotesSchemaType = {
  contact: {
    name: '',
    phone: '',
    phoneExt: '',
    email: '',
  },
  notes: '',
}

/**
 * Sanitize input and remove null values
 */
function getDefaultValues<T extends ContactWithNotesSchemaType>(
  values: Partial<T>
): ContactWithNotesSchemaType {
  return {
    contact: {
      name: values?.contact?.name ?? '',
      phone: values?.contact?.phone ?? '',
      phoneExt: values?.contact?.phoneExt ?? '',
      email: values?.contact?.email ?? '',
    },
    notes: values?.notes ?? '',
  }
}

export interface ICustomerContactFormProps
  extends IAddNewEntityFormProps<ContactWithNotesSchemaType> {}

export const CustomerContactForm: FC<ICustomerContactFormProps> = ({
  onSuccess: onSuccessProp,
  onCancel: onCancelProp = () => {},
  defaultValues: defaultValuesProp = emptyContactState,
  primaryButtonText: primaryButtonTextProp = 'Save Changes',
  isLoading: isLoadingProp,
}) => {
  const defaultValues = getDefaultValues(defaultValuesProp)

  const { control, errors, handleSubmit } = useForm({
    mode: 'all',
    resolver: yupResolver(ContactWithNotesSchema),
    context: {
      isContactInfoRequired: true,
    },
    defaultValues: ContactWithNotesSchema.cast({
      ...emptyContactState,
      ...defaultValues,
    }),
  })

  const submitHandler = (data: ContactWithNotesSchemaType) => {
    onSuccessProp(data)
  }

  return (
    <AddEntityForm onSubmit={handleSubmit(submitHandler)} dataTest='add-customer-contact-form'>
      {/* Because this is a custom `.test()` function, it doesn't appear in schema type */}
      {(errors?.contact as any)?.message && (
        <div className='mb4'>
          <InlineMessage
            type={INLINE_MESSAGE_VARIANTS.ALERT}
            message='Please add an email or phone.'
          />
        </div>
      )}
      <div className='rounded pa4 bg-secondary mb4'>
        {/* Customer contact name */}
        <div className='mb4'>
          <Controller
            name='contact.name'
            control={control}
            render={({ onBlur, onChange, value }) => {
              const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
                onChange(e.target.value)
              }

              return (
                <TextInput
                  label='Customer Contact Name'
                  value={value}
                  autoFocus
                  placeholder='Jane Doe'
                  onChange={changeHandler}
                  large
                  onBlur={onBlur}
                  validationMessage={errors?.contact?.name?.message}
                  invalid={!!errors?.contact?.name?.message}
                  dataTest='customer-contact-name-input'
                />
              )
            }}
          />
        </div>

        <div className='flex mb4'>
          <div className='flex-auto pr2'>
            {/* Customer contact phone */}
            <Controller
              name='contact.phone'
              control={control}
              render={({ onBlur, onChange, value }) => {
                const formattedValue = formatPhone(value, { removeCountryCode: '+1 ' })
                const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
                  onChange(unformatPhone(e.target.value))
                }

                return (
                  <PhoneInput
                    autoComplete='off'
                    label='Customer Contact Phone'
                    onChange={changeHandler}
                    onBlur={onBlur}
                    placeholder='(000) 000-0000'
                    value={formattedValue}
                    large
                    clearable={false}
                    validationMessage={errors?.contact?.phone?.message}
                    invalid={!!errors?.contact?.phone?.message}
                    dataTest='customer-contact-phone-input'
                    className='fs-mask'
                  />
                )
              }}
            />
          </div>
          <div className='w-40 pl2'>
            {/* Customer contact phone extension */}
            <Controller
              name='contact.phoneExt'
              control={control}
              render={({ onBlur, onChange, value }) => {
                const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
                  onChange(e.target.value)
                }

                return (
                  <TextInput
                    label='Extension'
                    value={value}
                    onChange={changeHandler}
                    large
                    onBlur={onBlur}
                    validationMessage={errors?.contact?.phoneExt?.message}
                    invalid={!!errors?.contact?.phoneExt?.message}
                    dataTest='customer-contact-phoneExt-input'
                  />
                )
              }}
            />
          </div>
        </div>

        {/* Customer contact email */}
        <div className='mb1'>
          <Controller
            name='contact.email'
            control={control}
            render={({ onBlur, onChange, value }) => {
              const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
                onChange(e.target.value)
              }

              return (
                <TextInput
                  label='Customer Contact Email'
                  value={value}
                  onChange={changeHandler}
                  placeholder='jane.doe@domain.com'
                  large
                  onBlur={onBlur}
                  validationMessage={errors?.contact?.email?.message}
                  invalid={!!errors?.contact?.email?.message}
                  dataTest='customer-contact-email-input'
                  className='fs-mask'
                />
              )
            }}
          />
        </div>
      </div>

      {/* Customer notes */}
      <div className='mb5'>
        <Controller
          name='notes'
          control={control}
          render={({ onBlur, onChange, value }) => {
            const changeHandler = (e: ChangeEvent<HTMLTextAreaElement>) => {
              onChange(e.target.value)
            }

            return (
              <TextArea
                placeholder='Write internal notes about this customer.'
                label='Customer Notes'
                size='large'
                dataTest='customer-notes-input'
                value={value}
                onBlur={onBlur}
                onChange={changeHandler}
              />
            )
          }}
        />
      </div>

      <AddEntityFormFooter>
        <Button
          color='tertiary'
          size='large'
          type='button'
          onClick={onCancelProp}
          data-test='cancel-new-customer'>
          Cancel
        </Button>
        <Button
          color='primary'
          disabled={isLoadingProp}
          size='large'
          type='submit'
          data-test='save-new-customer'>
          {primaryButtonTextProp}
        </Button>
      </AddEntityFormFooter>
    </AddEntityForm>
  )
}
